// /experience — Landing produit geoVINUM Experience.
// Structure narrative en 17 sections (Fev-2026) — pensée pour convertir
// autour du grand axe : « Arrêtez de vendre une bouteille. Vendez une
// expérience. » La phrase apparaît volontairement 3 fois seulement :
// hero, section 15 (pivot), manifeste 16.
// Contenu entièrement traduit FR / EN / ES / PT (dictionnaire local).
import { Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, KeyRound, Wine, Building2,
  GraduationCap, Tag, Map as MapIcon, Trophy, Leaf, Zap, Repeat,
  Lightbulb, Wrench, Share2, Smartphone, BarChart3, Users,
  ClipboardList, Play, Landmark, School, CheckCircle2, QrCode, Wand2, Puzzle,
} from "lucide-react";
import Layout from "@/components/Layout";
import SEO, {
  geovinumOrganizationJsonLd,
  faqPageJsonLd,
} from "@/components/SEO";
import { useLang } from "@/lib/i18n";

// CTAs — self-service builder vs. talk-to-team form.
const CREATE_URL = "https://app.geovinum.io/experience/new";
const CONTACT_URL = "/contact?subject=experience-custom";

// Case study screenshots — real Saint-Vincent Tournante event running
// on geoVINUM Experience. Uploaded by operator (23:41 / 00:21 batch).
const CASE_SCREENS = {
  billets: "https://customer-assets-7cd3h4nn.emergentagent.net/job_geo-vinum-preview/artifacts/o8tjqjr7_Captura%20de%20Tela%202026-09-22%20a%CC%80s%2000.21.16.webp",
  vins:    "https://customer-assets-7cd3h4nn.emergentagent.net/job_geo-vinum-preview/artifacts/ox5euzwl_Captura%20de%20Tela%202026-09-22%20a%CC%80s%2000.21.34.webp",
};

// ---------------------------------------------------------------------------
// Dictionary — every visible string in FR / EN / ES / PT.
// Kept in the file (rather than global i18n) because it's page-scoped:
// ~150 keys used nowhere else, and inlining lets translators see the
// full narrative in one place.
// ---------------------------------------------------------------------------
const COPY = {
  fr: {
    meta_title: "geoVINUM Experience — Arrêtez de vendre une bouteille. Vendez une expérience.",
    meta_desc:
      "Créez des expériences interactives autour du vin, des boissons, des marques et des territoires. Chasses au trésor, escape games, soirées dégustation, salons, masterclasses, brand activations : geoVINUM Experience transforme votre événement en aventure.",
    breadcrumb_home: "Accueil",
    // Hero
    hero_title_a: "Arrêtez de vendre une bouteille.",
    hero_title_b: "Vendez une expérience.",
    hero_desc_pre: "Transformez une dégustation, une visite, un salon, une soirée ou une activation de marque en ",
    hero_desc_strong: "expérience interactive et mémorable",
    hero_desc_post: ". Avec geoVINUM Experience, vous pouvez la créer vous-même — ou nous la construisons pour vous.",
    hero_diy_title: "La créer vous-même",
    hero_diy_desc: "Construisez votre expérience avec les outils geoVINUM.",
    hero_custom_title: "Nous la construisons pour vous",
    hero_custom_desc: "Notre équipe conçoit et déploie une expérience sur mesure, ponctuelle ou récurrente.",
    hero_cta_diy: "Créer mon expérience",
    hero_cta_custom: "Construire mon expérience avec geoVINUM",
    hero_ribbon: "Chasses au trésor · Escape games · Dégustations · Salons · Activations de marque · Et bien plus.",
    // 02 Le changement
    s2_eyebrow: "02 · Le changement",
    s2_title_a: "Les gens ne veulent plus seulement acheter.",
    s2_title_b: "Ils veulent vivre une expérience.",
    s2_intro: "Pendant longtemps, l'objectif était simple :",
    s2_flow: ["Présenter", "Faire déguster", "Vendre"],
    s2_pivot: "Mais une bouteille peut être oubliée.\nUne expérience, elle, peut être vécue, partagée et mémorisée.",
    s2_today: "Aujourd'hui, créez :",
    s2_chips: ["de la découverte", "du jeu", "de l'émotion", "de l'interaction", "de la connaissance", "du partage"],
    s2_signature: "Transformez votre produit en histoire.",
    // 03 Solution
    s3_eyebrow: "03 · La solution",
    s3_title_pre: "Bienvenue sur ",
    s3_title_em: "geoVINUM Experience.",
    s3_lead:
      "geoVINUM Experience est une plateforme et un service de conception permettant de créer des expériences interactives autour du vin, des boissons, des marques, des territoires et des événements. Vous avez deux façons de l'utiliser.",
    s3_diy_kicker: "🧩 01 — À faire soi-même",
    s3_diy_title: "Vous avez le concept.",
    s3_diy_body: "Vous créez vous-même votre expérience avec geoVINUM.",
    s3_diy_cta: "Commencer à créer",
    s3_custom_kicker: "✨ 02 — Nous le construisons pour vous",
    s3_custom_title: "Vous avez l'idée.",
    s3_custom_body: "Nous concevons l'expérience avec vous — ou pour vous.",
    s3_custom_cta: "Parler à notre équipe",
    // 04 Use cases
    s4_eyebrow: "04 · Ce que vous pouvez créer",
    s4_title_a: "Une plateforme.",
    s4_title_b: "Une infinité d'expériences.",
    s4_sub: "geoVINUM Experience ne se limite pas à la dégustation.",
    usecases: [
      { title: "Chasse au trésor",     body: "Faites explorer un domaine, une ville, un terroir ou une marque. Avec ou sans alcool." },
      { title: "Escape game",          body: "Transformez votre contenu en énigmes, missions, indices et défis." },
      { title: "Soirée dégustation",   body: "Ne faites plus simplement goûter. Faites découvrir, choisir, jouer, apprendre et partager." },
      { title: "Salon professionnel",  body: "Transformez votre stand en parcours interactif. Attirez → engagez → faites découvrir → créez la rencontre." },
      { title: "Masterclass",          body: "Transformez une présentation en expérience pédagogique." },
      { title: "Activation de marque", body: "Créez une expérience autour d'un produit, d'une marque ou d'un lancement." },
      { title: "Parcours territorial", body: "Faites découvrir un territoire, une appellation, une destination ou un patrimoine." },
      { title: "Challenges",           body: "Créez des expériences individuelles ou collectives avec points, équipes, quiz et récompenses." },
      { title: "Sans ou peu d'alcool", body: "Créez des expériences de découverte avec ou sans alcool." },
    ],
    // 05 Modes
    s5_eyebrow: "05 · Deux modes",
    s5_title_a: "Créez-la vous-même.",
    s5_title_b: "Ou laissez-nous la construire pour vous.",
    s5_diy_kicker: "🧩 En autonomie",
    s5_diy_title: "Vous avez les compétences. Le concept. L'envie d'aller vite.",
    s5_diy_body: "Avec geoVINUM, vous pouvez créer :",
    s5_diy_items: ["parcours", "contenus", "quiz", "jeux", "équipes", "challenges", "expériences de dégustation", "gamification"],
    s5_diy_you: "Vous construisez l'expérience.",
    s5_diy_cta: "Créer avec geoVINUM",
    s5_custom_kicker: "✨ Expérience sur mesure",
    s5_custom_title: "Vous voulez une expérience professionnelle, originale et sur mesure.",
    s5_custom_body: "Nous pouvons prendre en charge :",
    s5_custom_pipeline: ["Concept", "Storytelling", "UX / Parcours", "Contenus", "Gamification", "Expérience digitale", "Déploiement", "Animation"],
    s5_custom_cta: "Parlons de votre projet",
    // 06 Cadence
    s6_eyebrow: "06 · Cadence",
    s6_title_a: "Un seul événement.",
    s6_title_b: "Ou une expérience qui perdure.",
    s6_oneshot_title: "Ponctuel",
    s6_oneshot: ["une soirée", "une dégustation", "un lancement", "un salon", "une masterclass", "une activation", "une chasse au trésor", "un escape game"],
    s6_recurring_title: "Récurrent",
    s6_recurring: ["une expérience permanente", "un parcours touristique", "une animation mensuelle", "une expérience saisonnière", "un programme pédagogique", "une animation en domaine", "une expérience de marque"],
    s6_signature: "Construisez une fois. Vivez-la encore et encore.",
    // 07 How
    s7_eyebrow: "07 · Comment ça marche ?",
    s7_title_a: "De l'idée ",
    s7_title_b: "à l'expérience.",
    steps: [
      { num: "01", title: "Imaginer",    body: "Vous définissez votre objectif, votre public et votre concept." },
      { num: "02", title: "Construire",  body: "Vous le construisez vous-même ou nous le construisons pour vous." },
      { num: "03", title: "Partager",    body: "Votre expérience possède son propre lien. Diffusez-le : site · Instagram · LinkedIn · email · QR code · newsletter · billetterie." },
      { num: "04", title: "Vivre",       body: "Le participant utilise son smartphone pour vivre le parcours." },
      { num: "05", title: "Engager",     body: "Il peut : Découvrir → Choisir → Explorer → Jouer → Apprendre → Déguster → Partager." },
      { num: "06", title: "Mesurer",     body: "Vous suivez la participation et l'engagement." },
    ],
    // 08 Adventure
    s8_eyebrow: "08 · L'expérience en action",
    s8_title_a: "De l'événement",
    s8_title_b: "à l'aventure.",
    s8_before: "Avant",
    s8_after: "Avec geoVINUM Experience",
    before_flow: ["Arriver", "Écouter", "Déguster", "Repartir"],
    after_flow: ["Découvrir", "Choisir", "Explorer", "Jouer", "Déguster", "Apprendre", "Partager", "Se souvenir"],
    s8_signature_a: "Même événement.",
    s8_signature_b: "Expérience complètement différente.",
    // 09 Case
    s9_eyebrow: "09 · Exemple concret",
    s9_title_a: "Saint-Vincent Tournante ",
    s9_title_b: "de la Bourgogne.",
    s9_chip_a: "330 participants",
    s9_chip_b: "Un parcours de dégustation interactif",
    s9_part_title: "Les participants",
    s9_part_items: ["choisissent leur formule", "choisissent leur vin", "voient où il est servi", "présentent leur QR code", "dégustent", "découvrent le vin", "jouent au quiz", "gagnent des points", "rejoignent leur équipe", "progressent dans l'expérience"],
    s9_org_title: "Le professionnel gère",
    s9_org_items: ["participants", "créneaux", "vins", "stocks", "points", "équipes", "quiz", "scans", "statistiques"],
    s9_signature: "La dégustation devient un voyage.",
    // 10 Audiences
    s10_eyebrow: "10 · Pour qui ?",
    s10_title_a: "Pensé pour les créateurs",
    s10_title_b: "d'expériences.",
    audiences: [
      { emoji: "🍷", title: "Vignerons & domaines",       body: "Transformez vos dégustations et visites." },
      { emoji: "🏢", title: "Agences événementielles",    body: "Ajoutez une dimension interactive aux événements de vos clients." },
      { emoji: "🏷️", title: "Marques",                    body: "Créez des activations mémorables." },
      { emoji: "🎪", title: "Salons professionnels",      body: "Transformez les visiteurs en participants." },
      { emoji: "🗺️", title: "Territoires & destinations", body: "Faites découvrir un lieu autrement." },
      { emoji: "🎓", title: "Écoles & organismes",        body: "Transformez l'apprentissage en expérience." },
    ],
    // 11 Gains
    s11_eyebrow: "11 · Ce que vous gagnez",
    s11_title_a: "Ne captez pas juste l'attention.",
    s11_title_b: "Créez de l'engagement.",
    gains: [
      { title: "Engager",             body: "Transformez une audience passive en participants." },
      { title: "Différencier",        body: "Créez une expérience que les autres événements n'offrent pas." },
      { title: "Éduquer",             body: "Faites apprendre autrement." },
      { title: "Mémoriser",           body: "Créez des souvenirs associés à votre marque ou votre produit." },
      { title: "Réutiliser",          body: "Une expérience peut être ponctuelle ou réutilisée dans le temps." },
      { title: "Mesurer",             body: "Mesurez la participation et l'engagement." },
      { title: "Inventer de nouveaux moments de consommation", body: "Créez de nouvelles occasions de découvrir vos produits." },
    ],
    // 12 Ecosystem
    s12_eyebrow: "12 · L'écosystème geoVINUM",
    s12_title_a: "Plus qu'un simple outil",
    s12_title_b: "événementiel.",
    s12_sub: "geoVINUM Experience bénéficie de l'écosystème geoVINUM.",
    ecosystem: [
      { emoji: "🗺️", title: "Maps",       body: "Territoires & appellations" },
      { emoji: "📚", title: "Content",    body: "Connaissance des vins et boissons" },
      { emoji: "🎓", title: "Learning",   body: "Pédagogie & formation" },
      { emoji: "🎮", title: "Play",       body: "Quiz & gamification" },
      { emoji: "🍷", title: "Experience", body: "Événements & parcours interactifs" },
    ],
    s12_signature: "Un écosystème. Une couche d'expérience.",
    // 13 Why
    s13_eyebrow: "13 · Pourquoi geoVINUM ?",
    s13_title_a: "L'expérience est digitale.",
    s13_title_b: "Le souvenir est réel.",
    s13_intro: "geoVINUM combine :",
    why_stack: ["Culture du vin", "Technologie", "Contenus", "Gamification", "Design d'expérience"],
    s13_bullets: ["Ce n'est pas simplement un outil de billetterie.", "Ce n'est pas simplement un quiz.", "Ce n'est pas simplement une application."],
    s13_signature: "C'est un moteur d'expériences.",
    // 14 Distribution
    s14_eyebrow: "14 · Distribution",
    s14_title_a: "Votre expérience. Votre audience.",
    s14_title_b: "Votre lien.",
    s14_sub: "Une fois créée, votre expérience peut être diffusée partout.",
    distribution: ["Votre site web", "Vos réseaux sociaux", "Vos emails", "Vos newsletters", "Vos QR codes", "Vos campagnes", "Vos événements physiques"],
    s14_pro: "Le professionnel partage son lien geoVINUM Experience.",
    s14_participant: "Le participant entre directement dans l'expérience.",
    // 15 Pivot
    s15_kicker: "15 · Le vrai changement",
    s15_title_a: "Arrêtez de vendre la bouteille.",
    s15_title_b: "Commencez à vendre l'expérience.",
    pivot_pairs: [
      { bottle: "se vend.",           exp: "se vit." },
      { bottle: "se goûte.",          exp: "se découvre." },
      { bottle: "peut être oubliée.", exp: "peut être racontée." },
    ],
    s15_bottle: "Une bouteille",
    s15_exp: "Une expérience",
    s15_signature_a: "Le produit n'est plus la destination.",
    s15_signature_b: "Il fait partie du voyage.",
    // 16 Manifesto
    s16_eyebrow: "16 · Manifeste",
    s16_title_a: "Ne servez pas juste un verre.",
    s16_title_b: "Créez une aventure.",
    manifesto_pairs: [
      ["N'organisez pas juste un événement.",  "Créez une raison de participer."],
      ["Ne présentez pas juste un produit.",   "Créez une raison de le découvrir."],
      ["Ne vendez pas juste une bouteille.",   "Vendez l'expérience qui l'entoure."],
    ],
    // 17 Final CTA
    s17_eyebrow: "17 · Passez à l'action",
    s17_title_a: "Quelle expérience allez-vous",
    s17_title_b: "créer ?",
    s17_diy_title: "En autonomie",
    s17_diy_body: "Construisez votre propre expérience avec geoVINUM.",
    s17_diy_cta: "Commencer à créer",
    s17_custom_title: "Nous la construisons pour vous",
    s17_custom_body: "Dites-nous votre idée. Nous en ferons une expérience.",
    s17_custom_cta: "Construire mon expérience",
    // FAQ
    faq: [
      { q: "Qu'est-ce que geoVINUM Experience ?",         a: "geoVINUM Experience est à la fois une plateforme et un service de conception permettant de créer des expériences interactives autour du vin, des boissons, des marques, des territoires et des événements : chasses au trésor, escape games, soirées dégustation, salons, masterclasses, activations de marque, parcours territoriaux, challenges gamifiés." },
      { q: "Puis-je créer une expérience moi-même ?",     a: "Oui. Le mode en autonomie permet de construire seul votre expérience avec les outils geoVINUM : parcours, contenus, quiz, jeux, équipes, challenges, gamification, expériences de dégustation." },
      { q: "Puis-je vous demander de concevoir l'expérience pour moi ?", a: "Oui. En mode sur mesure, notre équipe prend en charge concept, storytelling, UX/parcours, contenus, gamification, expérience digitale, déploiement et animation. Idéal pour un événement à fort enjeu ou une opération de marque." },
      { q: "Une expérience peut-elle être réutilisée ?",  a: "Oui. Chaque expérience peut être ponctuelle (soirée, dégustation, salon, masterclass) ou récurrente (parcours touristique, animation mensuelle, expérience saisonnière, programme pédagogique, animation en domaine, expérience de marque). Construisez une fois, vivez-la encore et encore." },
      { q: "Comment le participant accède-t-il à l'expérience ?", a: "Chaque expérience possède son propre lien geoVINUM Experience. Le professionnel diffuse le lien via son site, ses réseaux sociaux, ses emails, sa newsletter, un QR code, une campagne ou un événement physique. Le participant l'ouvre sur smartphone et entre directement dans le parcours." },
      { q: "Comment se mesure l'engagement ?",            a: "geoVINUM Experience suit en temps réel les participants, créneaux, vins, stocks, points, équipes, quiz, scans QR et statistiques. Vous pilotez votre événement et mesurez son impact." },
      { q: "Peut-on créer des expériences sans alcool ?", a: "Oui. Le format « sans ou peu d'alcool » permet de créer des expériences de découverte avec ou sans alcool — utile pour un public jeune, une clientèle familiale ou une programmation dry." },
    ],
  },

  en: {
    meta_title: "geoVINUM Experience — Stop selling a bottle. Sell an experience.",
    meta_desc: "Create interactive experiences around wine, drinks, brands, territories and events. Treasure hunts, escape games, tasting nights, trade shows, masterclasses, brand activations: geoVINUM Experience turns your event into an adventure.",
    breadcrumb_home: "Home",
    hero_title_a: "Stop selling a bottle.",
    hero_title_b: "Sell an experience.",
    hero_desc_pre: "Turn a tasting, a visit, a trade show, an evening or a brand activation into an ",
    hero_desc_strong: "interactive, memorable experience",
    hero_desc_post: ". With geoVINUM Experience, you can build it yourself — or we can build it for you.",
    hero_diy_title: "Build it yourself",
    hero_diy_desc: "Design your experience with geoVINUM's toolkit.",
    hero_custom_title: "We build it for you",
    hero_custom_desc: "Our team designs and deploys a bespoke experience, one-off or recurring.",
    hero_cta_diy: "Create my experience",
    hero_cta_custom: "Build my experience with geoVINUM",
    hero_ribbon: "Treasure hunts · Escape games · Tasting experiences · Trade shows · Brand activations · And more.",
    s2_eyebrow: "02 · The shift",
    s2_title_a: "People don't just want to buy.",
    s2_title_b: "They want to experience.",
    s2_intro: "For a long time, the goal was simple:",
    s2_flow: ["Present", "Let them taste", "Sell"],
    s2_pivot: "But a bottle can be forgotten.\nAn experience can be lived, shared and remembered.",
    s2_today: "Today, create:",
    s2_chips: ["discovery", "play", "emotion", "interaction", "knowledge", "sharing"],
    s2_signature: "Turn your product into a story.",
    s3_eyebrow: "03 · The solution",
    s3_title_pre: "Welcome to ",
    s3_title_em: "geoVINUM Experience.",
    s3_lead: "geoVINUM Experience is both a platform and a design service that lets you create interactive experiences around wine, drinks, brands, territories and events. There are two ways to use it.",
    s3_diy_kicker: "🧩 01 — Do it yourself",
    s3_diy_title: "You have the concept.",
    s3_diy_body: "Build your experience yourself with geoVINUM.",
    s3_diy_cta: "Start creating",
    s3_custom_kicker: "✨ 02 — We build it for you",
    s3_custom_title: "You have the idea.",
    s3_custom_body: "We design the experience with you — or for you.",
    s3_custom_cta: "Talk to our team",
    s4_eyebrow: "04 · What you can create",
    s4_title_a: "One platform.",
    s4_title_b: "Endless experiences.",
    s4_sub: "geoVINUM Experience isn't limited to tasting.",
    usecases: [
      { title: "Treasure hunt",     body: "Have people explore an estate, a city, a terroir or a brand. With or without alcohol." },
      { title: "Escape game",       body: "Turn your content into puzzles, missions, clues and challenges." },
      { title: "Tasting night",     body: "Don't just let people taste. Let them discover, choose, play, learn and share." },
      { title: "Trade show",        body: "Turn your booth into an interactive journey. Attract → engage → let discover → create the encounter." },
      { title: "Masterclass",       body: "Turn a presentation into an educational experience." },
      { title: "Brand activation",  body: "Build an experience around a product, a brand or a launch." },
      { title: "Territorial trail", body: "Let visitors discover a territory, an appellation, a destination or a heritage site." },
      { title: "Challenges",        body: "Create individual or team experiences with points, teams, quizzes and rewards." },
      { title: "No / Low alcohol",  body: "Create discovery experiences with or without alcohol." },
    ],
    s5_eyebrow: "05 · Two modes",
    s5_title_a: "Create it yourself.",
    s5_title_b: "Or let us build it for you.",
    s5_diy_kicker: "🧩 Self-service",
    s5_diy_title: "You have the skills. You have the concept. You want to move fast.",
    s5_diy_body: "With geoVINUM, you can create:",
    s5_diy_items: ["journeys", "content", "quizzes", "games", "teams", "challenges", "tasting experiences", "gamification"],
    s5_diy_you: "You build the experience.",
    s5_diy_cta: "Create with geoVINUM",
    s5_custom_kicker: "✨ Custom experience",
    s5_custom_title: "You want a professional, original, tailor-made experience.",
    s5_custom_body: "We can handle:",
    s5_custom_pipeline: ["Concept", "Storytelling", "UX / journey", "Content", "Gamification", "Digital experience", "Deployment", "Live hosting"],
    s5_custom_cta: "Let's talk about your project",
    s6_eyebrow: "06 · Cadence",
    s6_title_a: "One event.",
    s6_title_b: "Or an experience that lives on.",
    s6_oneshot_title: "One-off",
    s6_oneshot: ["an evening", "a tasting", "a launch", "a trade show", "a masterclass", "an activation", "a treasure hunt", "an escape game"],
    s6_recurring_title: "Recurring",
    s6_recurring: ["a permanent experience", "a tourist trail", "a monthly activation", "a seasonal experience", "an educational programme", "on-site estate hosting", "a brand experience"],
    s6_signature: "Build once. Experience again and again.",
    s7_eyebrow: "07 · How it works",
    s7_title_a: "From idea ",
    s7_title_b: "to experience.",
    steps: [
      { num: "01", title: "Imagine",   body: "Define your goal, your audience and your concept." },
      { num: "02", title: "Build",     body: "Build it yourself, or we build it for you." },
      { num: "03", title: "Share",     body: "Your experience has its own link. Share it: website · Instagram · LinkedIn · email · QR code · newsletter · ticketing." },
      { num: "04", title: "Experience",body: "Participants use their smartphone to live the journey." },
      { num: "05", title: "Engage",    body: "They can: Discover → Choose → Explore → Play → Learn → Taste → Share." },
      { num: "06", title: "Measure",   body: "Track participation and engagement." },
    ],
    s8_eyebrow: "08 · Experience in action",
    s8_title_a: "From event",
    s8_title_b: "to adventure.",
    s8_before: "Before",
    s8_after: "With geoVINUM Experience",
    before_flow: ["Arrive", "Listen", "Taste", "Leave"],
    after_flow: ["Discover", "Choose", "Explore", "Play", "Taste", "Learn", "Connect", "Remember"],
    s8_signature_a: "Same event.",
    s8_signature_b: "Completely different experience.",
    s9_eyebrow: "09 · Real-world example",
    s9_title_a: "Saint-Vincent Tournante ",
    s9_title_b: "of Burgundy.",
    s9_chip_a: "330 participants",
    s9_chip_b: "One interactive tasting journey",
    s9_part_title: "Participants",
    s9_part_items: ["pick their package", "choose their wine", "see where it's poured", "show their QR code", "taste", "discover the wine", "play the quiz", "earn points", "join their team", "progress through the experience"],
    s9_org_title: "The organiser manages",
    s9_org_items: ["participants", "time slots", "wines", "stock", "points", "teams", "quizzes", "scans", "analytics"],
    s9_signature: "The tasting becomes a journey.",
    s10_eyebrow: "10 · Who it's for",
    s10_title_a: "Built for",
    s10_title_b: "experience creators.",
    audiences: [
      { emoji: "🍷", title: "Winemakers & estates",     body: "Transform your tastings and visits." },
      { emoji: "🏢", title: "Event agencies",           body: "Add an interactive layer to your clients' events." },
      { emoji: "🏷️", title: "Brands",                   body: "Create memorable activations." },
      { emoji: "🎪", title: "Trade shows",              body: "Turn visitors into participants." },
      { emoji: "🗺️", title: "Territories & destinations", body: "Let people discover a place differently." },
      { emoji: "🎓", title: "Schools & training bodies", body: "Turn learning into an experience." },
    ],
    s11_eyebrow: "11 · What you gain",
    s11_title_a: "Don't just get attention.",
    s11_title_b: "Create engagement.",
    gains: [
      { title: "Engage",       body: "Turn a passive audience into participants." },
      { title: "Differentiate",body: "Create an experience other events don't offer." },
      { title: "Educate",      body: "Teach differently." },
      { title: "Memorise",     body: "Build memories tied to your brand or product." },
      { title: "Reuse",        body: "An experience can be one-off or reused over time." },
      { title: "Measure",      body: "Measure participation and engagement." },
      { title: "Invent new consumption moments", body: "Create new occasions to discover your products." },
    ],
    s12_eyebrow: "12 · The geoVINUM ecosystem",
    s12_title_a: "More than",
    s12_title_b: "an event tool.",
    s12_sub: "geoVINUM Experience taps into the wider geoVINUM ecosystem.",
    ecosystem: [
      { emoji: "🗺️", title: "Maps",       body: "Territories & appellations" },
      { emoji: "📚", title: "Content",    body: "Wine and drinks knowledge" },
      { emoji: "🎓", title: "Learning",   body: "Education & training" },
      { emoji: "🎮", title: "Play",       body: "Quizzes & gamification" },
      { emoji: "🍷", title: "Experience", body: "Events & interactive journeys" },
    ],
    s12_signature: "One ecosystem. One experience layer.",
    s13_eyebrow: "13 · Why geoVINUM",
    s13_title_a: "The experience is digital.",
    s13_title_b: "The memory is real.",
    s13_intro: "geoVINUM combines:",
    why_stack: ["Wine culture", "Technology", "Content", "Gamification", "Experience design"],
    s13_bullets: ["It's not just a ticketing tool.", "It's not just a quiz.", "It's not just an app."],
    s13_signature: "It's an experience engine.",
    s14_eyebrow: "14 · Distribution",
    s14_title_a: "Your experience. Your audience.",
    s14_title_b: "Your link.",
    s14_sub: "Once created, your experience can be distributed anywhere.",
    distribution: ["Your website", "Your social media", "Your emails", "Your newsletters", "Your QR codes", "Your campaigns", "Your physical events"],
    s14_pro: "The organiser shares their geoVINUM Experience link.",
    s14_participant: "The participant walks straight into the experience.",
    s15_kicker: "15 · The real shift",
    s15_title_a: "Stop selling the bottle.",
    s15_title_b: "Start selling the experience.",
    pivot_pairs: [
      { bottle: "is sold.",           exp: "is lived." },
      { bottle: "is tasted.",         exp: "is discovered." },
      { bottle: "can be forgotten.",  exp: "can be told." },
    ],
    s15_bottle: "A bottle",
    s15_exp: "An experience",
    s15_signature_a: "The product is no longer the destination.",
    s15_signature_b: "It becomes part of the journey.",
    s16_eyebrow: "16 · Manifesto",
    s16_title_a: "Don't just serve a drink.",
    s16_title_b: "Create an adventure.",
    manifesto_pairs: [
      ["Don't just organise an event.",   "Create a reason to take part."],
      ["Don't just present a product.",   "Create a reason to discover it."],
      ["Don't just sell a bottle.",       "Sell the experience around it."],
    ],
    s17_eyebrow: "17 · Take action",
    s17_title_a: "What experience",
    s17_title_b: "will you create?",
    s17_diy_title: "Do it yourself",
    s17_diy_body: "Build your own experience with geoVINUM.",
    s17_diy_cta: "Start creating",
    s17_custom_title: "We build it for you",
    s17_custom_body: "Tell us your idea. We'll turn it into an experience.",
    s17_custom_cta: "Build my experience",
    faq: [
      { q: "What is geoVINUM Experience?",                  a: "geoVINUM Experience is both a platform and a design service that lets you create interactive experiences around wine, drinks, brands, territories and events: treasure hunts, escape games, tasting nights, trade shows, masterclasses, brand activations, territorial trails, gamified challenges." },
      { q: "Can I build an experience myself?",             a: "Yes. Self-service mode lets you build your experience on your own with geoVINUM's toolkit: journeys, content, quizzes, games, teams, challenges, gamification, tasting experiences." },
      { q: "Can I ask you to design the experience for me?",a: "Yes. In custom mode, our team handles concept, storytelling, UX/journey, content, gamification, digital experience, deployment and live hosting. Ideal for high-stakes events or brand operations." },
      { q: "Can an experience be reused?",                  a: "Yes. Every experience can be one-off (evening, tasting, trade show, masterclass) or recurring (tourist trail, monthly activation, seasonal experience, educational programme, estate hosting, brand experience). Build once. Experience again and again." },
      { q: "How does the participant access the experience?",a: "Each experience has its own geoVINUM Experience link. You share it via your website, social media, emails, newsletter, a QR code, a campaign or a physical event. Participants open it on smartphone and walk straight into the journey." },
      { q: "How is engagement measured?",                   a: "geoVINUM Experience tracks participants, time slots, wines, stock, points, teams, quizzes, QR scans and analytics in real time. You pilot the event and measure its impact." },
      { q: "Can I create alcohol-free experiences?",        a: "Yes. The 'No / Low' format lets you create discovery experiences with or without alcohol — useful for a young audience, a family crowd or a dry programme." },
    ],
  },

  es: {
    meta_title: "geoVINUM Experience — Deja de vender una botella. Vende una experiencia.",
    meta_desc: "Crea experiencias interactivas en torno al vino, las bebidas, las marcas y los territorios. Búsquedas del tesoro, escape games, catas, ferias, masterclasses, activaciones de marca: geoVINUM Experience convierte tu evento en una aventura.",
    breadcrumb_home: "Inicio",
    hero_title_a: "Deja de vender una botella.",
    hero_title_b: "Vende una experiencia.",
    hero_desc_pre: "Convierte una cata, una visita, una feria, una velada o una activación de marca en una ",
    hero_desc_strong: "experiencia interactiva e inolvidable",
    hero_desc_post: ". Con geoVINUM Experience, puedes crearla tú mismo — o la construimos por ti.",
    hero_diy_title: "Créala tú mismo",
    hero_diy_desc: "Diseña tu experiencia con las herramientas geoVINUM.",
    hero_custom_title: "La construimos por ti",
    hero_custom_desc: "Nuestro equipo diseña y despliega una experiencia a medida, puntual o recurrente.",
    hero_cta_diy: "Crear mi experiencia",
    hero_cta_custom: "Construir mi experiencia con geoVINUM",
    hero_ribbon: "Búsquedas del tesoro · Escape games · Catas · Ferias · Activaciones de marca · Y mucho más.",
    s2_eyebrow: "02 · El cambio",
    s2_title_a: "La gente ya no quiere solo comprar.",
    s2_title_b: "Quiere vivir una experiencia.",
    s2_intro: "Durante mucho tiempo, el objetivo era simple:",
    s2_flow: ["Presentar", "Dar a probar", "Vender"],
    s2_pivot: "Pero una botella puede olvidarse.\nUna experiencia se vive, se comparte y se recuerda.",
    s2_today: "Hoy, crea:",
    s2_chips: ["descubrimiento", "juego", "emoción", "interacción", "conocimiento", "compartir"],
    s2_signature: "Convierte tu producto en una historia.",
    s3_eyebrow: "03 · La solución",
    s3_title_pre: "Bienvenido a ",
    s3_title_em: "geoVINUM Experience.",
    s3_lead: "geoVINUM Experience es a la vez una plataforma y un servicio de diseño que te permite crear experiencias interactivas en torno al vino, las bebidas, las marcas, los territorios y los eventos. Hay dos formas de usarlo.",
    s3_diy_kicker: "🧩 01 — Hazlo tú mismo",
    s3_diy_title: "Tienes el concepto.",
    s3_diy_body: "Construye tu experiencia por tu cuenta con geoVINUM.",
    s3_diy_cta: "Empezar a crear",
    s3_custom_kicker: "✨ 02 — Lo construimos por ti",
    s3_custom_title: "Tienes la idea.",
    s3_custom_body: "Diseñamos la experiencia contigo — o por ti.",
    s3_custom_cta: "Hablar con nuestro equipo",
    s4_eyebrow: "04 · Lo que puedes crear",
    s4_title_a: "Una plataforma.",
    s4_title_b: "Infinitas experiencias.",
    s4_sub: "geoVINUM Experience no se limita a la cata.",
    usecases: [
      { title: "Búsqueda del tesoro",   body: "Haz que se explore una bodega, una ciudad, un terroir o una marca. Con o sin alcohol." },
      { title: "Escape game",           body: "Convierte tu contenido en enigmas, misiones, pistas y desafíos." },
      { title: "Velada de cata",        body: "Ya no basta con dar a probar. Haz descubrir, elegir, jugar, aprender y compartir." },
      { title: "Feria profesional",     body: "Convierte tu stand en un recorrido interactivo. Atraer → involucrar → descubrir → generar encuentro." },
      { title: "Masterclass",           body: "Convierte una presentación en una experiencia pedagógica." },
      { title: "Activación de marca",   body: "Crea una experiencia en torno a un producto, una marca o un lanzamiento." },
      { title: "Recorrido territorial", body: "Haz descubrir un territorio, una denominación, un destino o un patrimonio." },
      { title: "Retos",                 body: "Crea experiencias individuales o colectivas con puntos, equipos, quizzes y recompensas." },
      { title: "Sin o con poco alcohol",body: "Crea experiencias de descubrimiento con o sin alcohol." },
    ],
    s5_eyebrow: "05 · Dos modos",
    s5_title_a: "Créala tú mismo.",
    s5_title_b: "O deja que la construyamos por ti.",
    s5_diy_kicker: "🧩 Autoservicio",
    s5_diy_title: "Tienes las competencias. El concepto. Ganas de ir rápido.",
    s5_diy_body: "Con geoVINUM, puedes crear:",
    s5_diy_items: ["recorridos", "contenidos", "quizzes", "juegos", "equipos", "retos", "experiencias de cata", "gamificación"],
    s5_diy_you: "Tú construyes la experiencia.",
    s5_diy_cta: "Crear con geoVINUM",
    s5_custom_kicker: "✨ Experiencia a medida",
    s5_custom_title: "Quieres una experiencia profesional, original y a medida.",
    s5_custom_body: "Podemos encargarnos de:",
    s5_custom_pipeline: ["Concepto", "Storytelling", "UX / Recorrido", "Contenidos", "Gamificación", "Experiencia digital", "Despliegue", "Animación"],
    s5_custom_cta: "Hablemos de tu proyecto",
    s6_eyebrow: "06 · Cadencia",
    s6_title_a: "Un evento.",
    s6_title_b: "O una experiencia que perdura.",
    s6_oneshot_title: "Puntual",
    s6_oneshot: ["una velada", "una cata", "un lanzamiento", "una feria", "una masterclass", "una activación", "una búsqueda del tesoro", "un escape game"],
    s6_recurring_title: "Recurrente",
    s6_recurring: ["una experiencia permanente", "una ruta turística", "una animación mensual", "una experiencia estacional", "un programa pedagógico", "animación en bodega", "una experiencia de marca"],
    s6_signature: "Constrúyela una vez. Vívela una y otra vez.",
    s7_eyebrow: "07 · Cómo funciona",
    s7_title_a: "De la idea ",
    s7_title_b: "a la experiencia.",
    steps: [
      { num: "01", title: "Imaginar",  body: "Define tu objetivo, tu público y tu concepto." },
      { num: "02", title: "Construir", body: "Lo construyes tú o lo construimos nosotros." },
      { num: "03", title: "Compartir", body: "Tu experiencia tiene su propio enlace. Difúndelo: web · Instagram · LinkedIn · email · QR · newsletter · ticketing." },
      { num: "04", title: "Vivirla",   body: "El participante usa su smartphone para vivir el recorrido." },
      { num: "05", title: "Engancharse",body: "Puede: Descubrir → Elegir → Explorar → Jugar → Aprender → Catar → Compartir." },
      { num: "06", title: "Medir",     body: "Sigue la participación y el engagement." },
    ],
    s8_eyebrow: "08 · La experiencia en acción",
    s8_title_a: "Del evento",
    s8_title_b: "a la aventura.",
    s8_before: "Antes",
    s8_after: "Con geoVINUM Experience",
    before_flow: ["Llegar", "Escuchar", "Catar", "Marcharse"],
    after_flow: ["Descubrir", "Elegir", "Explorar", "Jugar", "Catar", "Aprender", "Conectar", "Recordar"],
    s8_signature_a: "Mismo evento.",
    s8_signature_b: "Experiencia completamente diferente.",
    s9_eyebrow: "09 · Ejemplo real",
    s9_title_a: "Saint-Vincent Tournante ",
    s9_title_b: "de Borgoña.",
    s9_chip_a: "330 participantes",
    s9_chip_b: "Un recorrido de cata interactivo",
    s9_part_title: "Los participantes",
    s9_part_items: ["eligen su fórmula", "eligen su vino", "ven dónde se sirve", "muestran su QR", "catan", "descubren el vino", "juegan al quiz", "ganan puntos", "se suman a su equipo", "avanzan en la experiencia"],
    s9_org_title: "El profesional gestiona",
    s9_org_items: ["participantes", "franjas", "vinos", "stock", "puntos", "equipos", "quizzes", "escaneos", "estadísticas"],
    s9_signature: "La cata se convierte en un viaje.",
    s10_eyebrow: "10 · Para quién",
    s10_title_a: "Hecho para los creadores",
    s10_title_b: "de experiencias.",
    audiences: [
      { emoji: "🍷", title: "Bodegas y viticultores",       body: "Transforma tus catas y visitas." },
      { emoji: "🏢", title: "Agencias de eventos",           body: "Añade una capa interactiva a los eventos de tus clientes." },
      { emoji: "🏷️", title: "Marcas",                        body: "Crea activaciones memorables." },
      { emoji: "🎪", title: "Ferias profesionales",          body: "Convierte visitantes en participantes." },
      { emoji: "🗺️", title: "Territorios y destinos",        body: "Haz descubrir un lugar de otra forma." },
      { emoji: "🎓", title: "Escuelas y organismos",         body: "Convierte el aprendizaje en experiencia." },
    ],
    s11_eyebrow: "11 · Lo que ganas",
    s11_title_a: "No te limites a captar atención.",
    s11_title_b: "Genera engagement.",
    gains: [
      { title: "Enganchar",       body: "Convierte una audiencia pasiva en participantes." },
      { title: "Diferenciarte",   body: "Crea una experiencia que otros eventos no ofrecen." },
      { title: "Educar",          body: "Enseña de otra forma." },
      { title: "Grabar en la memoria", body: "Crea recuerdos asociados a tu marca o producto." },
      { title: "Reutilizar",      body: "Una experiencia puede ser puntual o reutilizarse en el tiempo." },
      { title: "Medir",           body: "Mide la participación y el engagement." },
      { title: "Inventar nuevos momentos de consumo", body: "Crea nuevas ocasiones para descubrir tus productos." },
    ],
    s12_eyebrow: "12 · El ecosistema geoVINUM",
    s12_title_a: "Más que una herramienta",
    s12_title_b: "de eventos.",
    s12_sub: "geoVINUM Experience se apoya en el ecosistema geoVINUM.",
    ecosystem: [
      { emoji: "🗺️", title: "Maps",       body: "Territorios y denominaciones" },
      { emoji: "📚", title: "Content",    body: "Conocimiento del vino y las bebidas" },
      { emoji: "🎓", title: "Learning",   body: "Pedagogía y formación" },
      { emoji: "🎮", title: "Play",       body: "Quizzes y gamificación" },
      { emoji: "🍷", title: "Experience", body: "Eventos y recorridos interactivos" },
    ],
    s12_signature: "Un ecosistema. Una capa de experiencia.",
    s13_eyebrow: "13 · Por qué geoVINUM",
    s13_title_a: "La experiencia es digital.",
    s13_title_b: "El recuerdo es real.",
    s13_intro: "geoVINUM combina:",
    why_stack: ["Cultura del vino", "Tecnología", "Contenidos", "Gamificación", "Diseño de experiencias"],
    s13_bullets: ["No es solo una herramienta de ticketing.", "No es solo un quiz.", "No es solo una app."],
    s13_signature: "Es un motor de experiencias.",
    s14_eyebrow: "14 · Distribución",
    s14_title_a: "Tu experiencia. Tu audiencia.",
    s14_title_b: "Tu enlace.",
    s14_sub: "Una vez creada, tu experiencia puede difundirse en cualquier parte.",
    distribution: ["Tu web", "Tus redes sociales", "Tus emails", "Tus newsletters", "Tus QR", "Tus campañas", "Tus eventos físicos"],
    s14_pro: "El profesional comparte su enlace geoVINUM Experience.",
    s14_participant: "El participante entra directamente en la experiencia.",
    s15_kicker: "15 · El verdadero cambio",
    s15_title_a: "Deja de vender la botella.",
    s15_title_b: "Empieza a vender la experiencia.",
    pivot_pairs: [
      { bottle: "se vende.",           exp: "se vive." },
      { bottle: "se cata.",            exp: "se descubre." },
      { bottle: "puede olvidarse.",    exp: "puede contarse." },
    ],
    s15_bottle: "Una botella",
    s15_exp: "Una experiencia",
    s15_signature_a: "El producto ya no es el destino.",
    s15_signature_b: "Forma parte del viaje.",
    s16_eyebrow: "16 · Manifiesto",
    s16_title_a: "No sirvas solo una copa.",
    s16_title_b: "Crea una aventura.",
    manifesto_pairs: [
      ["No organices solo un evento.", "Crea una razón para participar."],
      ["No presentes solo un producto.", "Crea una razón para descubrirlo."],
      ["No vendas solo una botella.",  "Vende la experiencia que la rodea."],
    ],
    s17_eyebrow: "17 · Pasa a la acción",
    s17_title_a: "¿Qué experiencia",
    s17_title_b: "vas a crear?",
    s17_diy_title: "Hazlo tú mismo",
    s17_diy_body: "Construye tu propia experiencia con geoVINUM.",
    s17_diy_cta: "Empezar a crear",
    s17_custom_title: "La construimos por ti",
    s17_custom_body: "Cuéntanos tu idea. La convertiremos en una experiencia.",
    s17_custom_cta: "Construir mi experiencia",
    faq: [
      { q: "¿Qué es geoVINUM Experience?",                a: "geoVINUM Experience es a la vez una plataforma y un servicio de diseño que permite crear experiencias interactivas en torno al vino, las bebidas, las marcas, los territorios y los eventos: búsquedas del tesoro, escape games, catas, ferias, masterclasses, activaciones de marca, recorridos territoriales, retos gamificados." },
      { q: "¿Puedo crear una experiencia yo mismo?",      a: "Sí. El modo autoservicio permite construir tu experiencia por tu cuenta con las herramientas geoVINUM: recorridos, contenidos, quizzes, juegos, equipos, retos, gamificación, experiencias de cata." },
      { q: "¿Podéis diseñar la experiencia por mí?",      a: "Sí. En modo a medida, nuestro equipo se encarga del concepto, storytelling, UX/recorrido, contenidos, gamificación, experiencia digital, despliegue y animación. Ideal para un evento de gran envergadura o una operación de marca." },
      { q: "¿Se puede reutilizar una experiencia?",       a: "Sí. Cada experiencia puede ser puntual (velada, cata, feria, masterclass) o recurrente (ruta turística, animación mensual, experiencia estacional, programa pedagógico, animación en bodega, experiencia de marca). Constrúyela una vez. Vívela una y otra vez." },
      { q: "¿Cómo accede el participante?",               a: "Cada experiencia tiene su propio enlace geoVINUM Experience. El profesional lo difunde a través de su web, redes sociales, emails, newsletter, un QR, una campaña o un evento físico. El participante lo abre en el móvil y entra directamente en el recorrido." },
      { q: "¿Cómo se mide el engagement?",                a: "geoVINUM Experience sigue en tiempo real los participantes, franjas, vinos, stock, puntos, equipos, quizzes, escaneos QR y estadísticas. Tú pilotas el evento y mides su impacto." },
      { q: "¿Se pueden crear experiencias sin alcohol?",  a: "Sí. El formato «sin o con poco alcohol» permite crear experiencias de descubrimiento con o sin alcohol — útil para un público joven, familiar o una programación dry." },
    ],
  },

  pt: {
    meta_title: "geoVINUM Experience — Pare de vender uma garrafa. Venda uma experiência.",
    meta_desc: "Crie experiências interativas em torno do vinho, das bebidas, das marcas e dos territórios. Caças ao tesouro, escape games, provas, feiras, masterclasses, ativações de marca: geoVINUM Experience transforma o seu evento numa aventura.",
    breadcrumb_home: "Início",
    hero_title_a: "Pare de vender uma garrafa.",
    hero_title_b: "Venda uma experiência.",
    hero_desc_pre: "Transforme uma prova, uma visita, uma feira, uma noite ou uma ativação de marca numa ",
    hero_desc_strong: "experiência interativa e memorável",
    hero_desc_post: ". Com o geoVINUM Experience, pode criá-la o próprio — ou nós construímo-la para si.",
    hero_diy_title: "Crie-a o próprio",
    hero_diy_desc: "Construa a sua experiência com as ferramentas geoVINUM.",
    hero_custom_title: "Construímo-la para si",
    hero_custom_desc: "A nossa equipa desenha e implementa uma experiência à medida, pontual ou recorrente.",
    hero_cta_diy: "Criar a minha experiência",
    hero_cta_custom: "Construir a minha experiência com a geoVINUM",
    hero_ribbon: "Caças ao tesouro · Escape games · Provas · Feiras · Ativações de marca · E muito mais.",
    s2_eyebrow: "02 · A mudança",
    s2_title_a: "As pessoas já não querem apenas comprar.",
    s2_title_b: "Querem viver uma experiência.",
    s2_intro: "Durante muito tempo, o objetivo era simples:",
    s2_flow: ["Apresentar", "Fazer provar", "Vender"],
    s2_pivot: "Mas uma garrafa pode ser esquecida.\nUma experiência é vivida, partilhada e memorizada.",
    s2_today: "Hoje, crie:",
    s2_chips: ["descoberta", "jogo", "emoção", "interação", "conhecimento", "partilha"],
    s2_signature: "Transforme o seu produto numa história.",
    s3_eyebrow: "03 · A solução",
    s3_title_pre: "Bem-vindo à ",
    s3_title_em: "geoVINUM Experience.",
    s3_lead: "geoVINUM Experience é ao mesmo tempo uma plataforma e um serviço de design que permite criar experiências interativas em torno do vinho, das bebidas, das marcas, dos territórios e dos eventos. Tem duas formas de a utilizar.",
    s3_diy_kicker: "🧩 01 — Faça o próprio",
    s3_diy_title: "Tem o conceito.",
    s3_diy_body: "Construa a sua experiência sozinho com a geoVINUM.",
    s3_diy_cta: "Começar a criar",
    s3_custom_kicker: "✨ 02 — Construímo-la para si",
    s3_custom_title: "Tem a ideia.",
    s3_custom_body: "Desenhamos a experiência consigo — ou por si.",
    s3_custom_cta: "Falar com a nossa equipa",
    s4_eyebrow: "04 · O que pode criar",
    s4_title_a: "Uma plataforma.",
    s4_title_b: "Experiências sem fim.",
    s4_sub: "geoVINUM Experience não se limita à prova.",
    usecases: [
      { title: "Caça ao tesouro",     body: "Faça explorar uma quinta, uma cidade, um terroir ou uma marca. Com ou sem álcool." },
      { title: "Escape game",         body: "Transforme o seu conteúdo em enigmas, missões, pistas e desafios." },
      { title: "Noite de prova",      body: "Deixe de apenas fazer provar. Faça descobrir, escolher, jogar, aprender e partilhar." },
      { title: "Feira profissional",  body: "Transforme o seu stand num percurso interativo. Atrair → envolver → fazer descobrir → criar o encontro." },
      { title: "Masterclass",         body: "Transforme uma apresentação numa experiência pedagógica." },
      { title: "Ativação de marca",   body: "Crie uma experiência em torno de um produto, uma marca ou um lançamento." },
      { title: "Percurso territorial",body: "Faça descobrir um território, uma denominação, um destino ou um património." },
      { title: "Desafios",            body: "Crie experiências individuais ou coletivas com pontos, equipas, quizzes e recompensas." },
      { title: "Sem ou pouco álcool", body: "Crie experiências de descoberta com ou sem álcool." },
    ],
    s5_eyebrow: "05 · Dois modos",
    s5_title_a: "Crie-a o próprio.",
    s5_title_b: "Ou deixe-nos construí-la por si.",
    s5_diy_kicker: "🧩 Self-service",
    s5_diy_title: "Tem as competências. O conceito. Quer ir depressa.",
    s5_diy_body: "Com a geoVINUM, pode criar:",
    s5_diy_items: ["percursos", "conteúdos", "quizzes", "jogos", "equipas", "desafios", "experiências de prova", "gamificação"],
    s5_diy_you: "Você constrói a experiência.",
    s5_diy_cta: "Criar com a geoVINUM",
    s5_custom_kicker: "✨ Experiência à medida",
    s5_custom_title: "Quer uma experiência profissional, original e à medida.",
    s5_custom_body: "Podemos tratar de:",
    s5_custom_pipeline: ["Conceito", "Storytelling", "UX / Percurso", "Conteúdos", "Gamificação", "Experiência digital", "Implementação", "Animação"],
    s5_custom_cta: "Falemos do seu projeto",
    s6_eyebrow: "06 · Cadência",
    s6_title_a: "Um evento.",
    s6_title_b: "Ou uma experiência que perdura.",
    s6_oneshot_title: "Pontual",
    s6_oneshot: ["uma noite", "uma prova", "um lançamento", "uma feira", "uma masterclass", "uma ativação", "uma caça ao tesouro", "um escape game"],
    s6_recurring_title: "Recorrente",
    s6_recurring: ["uma experiência permanente", "um percurso turístico", "uma animação mensal", "uma experiência sazonal", "um programa pedagógico", "animação em quinta", "uma experiência de marca"],
    s6_signature: "Construa uma vez. Viva-a de novo e de novo.",
    s7_eyebrow: "07 · Como funciona",
    s7_title_a: "Da ideia ",
    s7_title_b: "à experiência.",
    steps: [
      { num: "01", title: "Imaginar",   body: "Define o seu objetivo, o seu público e o seu conceito." },
      { num: "02", title: "Construir",  body: "Constrói o próprio ou construímos por si." },
      { num: "03", title: "Partilhar",  body: "A sua experiência tem o seu próprio link. Difunda-o: site · Instagram · LinkedIn · email · QR · newsletter · bilheteira." },
      { num: "04", title: "Viver",      body: "O participante usa o smartphone para viver o percurso." },
      { num: "05", title: "Envolver",   body: "Pode: Descobrir → Escolher → Explorar → Jogar → Aprender → Provar → Partilhar." },
      { num: "06", title: "Medir",      body: "Acompanhe a participação e o engagement." },
    ],
    s8_eyebrow: "08 · A experiência em ação",
    s8_title_a: "Do evento",
    s8_title_b: "à aventura.",
    s8_before: "Antes",
    s8_after: "Com a geoVINUM Experience",
    before_flow: ["Chegar", "Ouvir", "Provar", "Sair"],
    after_flow: ["Descobrir", "Escolher", "Explorar", "Jogar", "Provar", "Aprender", "Ligar-se", "Recordar"],
    s8_signature_a: "Mesmo evento.",
    s8_signature_b: "Experiência completamente diferente.",
    s9_eyebrow: "09 · Exemplo real",
    s9_title_a: "Saint-Vincent Tournante ",
    s9_title_b: "da Borgonha.",
    s9_chip_a: "330 participantes",
    s9_chip_b: "Um percurso de prova interativo",
    s9_part_title: "Os participantes",
    s9_part_items: ["escolhem a sua fórmula", "escolhem o seu vinho", "veem onde é servido", "apresentam o seu QR", "provam", "descobrem o vinho", "jogam o quiz", "ganham pontos", "juntam-se à equipa", "avançam na experiência"],
    s9_org_title: "O profissional gere",
    s9_org_items: ["participantes", "horários", "vinhos", "stocks", "pontos", "equipas", "quizzes", "leituras", "estatísticas"],
    s9_signature: "A prova torna-se uma viagem.",
    s10_eyebrow: "10 · Para quem",
    s10_title_a: "Feito para os criadores",
    s10_title_b: "de experiências.",
    audiences: [
      { emoji: "🍷", title: "Produtores e quintas",       body: "Transforme as suas provas e visitas." },
      { emoji: "🏢", title: "Agências de eventos",        body: "Adicione uma dimensão interativa aos eventos dos seus clientes." },
      { emoji: "🏷️", title: "Marcas",                     body: "Crie ativações memoráveis." },
      { emoji: "🎪", title: "Feiras profissionais",       body: "Transforme visitantes em participantes." },
      { emoji: "🗺️", title: "Territórios e destinos",     body: "Faça descobrir um lugar de outra forma." },
      { emoji: "🎓", title: "Escolas e organismos",       body: "Transforme a aprendizagem em experiência." },
    ],
    s11_eyebrow: "11 · O que ganha",
    s11_title_a: "Não capte apenas atenção.",
    s11_title_b: "Crie engagement.",
    gains: [
      { title: "Envolver",       body: "Transforme uma audiência passiva em participantes." },
      { title: "Diferenciar-se", body: "Crie uma experiência que outros eventos não oferecem." },
      { title: "Educar",         body: "Ensine de outra forma." },
      { title: "Memorizar",      body: "Crie memórias associadas à sua marca ou produto." },
      { title: "Reutilizar",     body: "Uma experiência pode ser pontual ou reutilizada ao longo do tempo." },
      { title: "Medir",          body: "Meça a participação e o engagement." },
      { title: "Inventar novos momentos de consumo", body: "Crie novas ocasiões para descobrir os seus produtos." },
    ],
    s12_eyebrow: "12 · O ecossistema geoVINUM",
    s12_title_a: "Mais do que uma ferramenta",
    s12_title_b: "de eventos.",
    s12_sub: "geoVINUM Experience beneficia do ecossistema geoVINUM.",
    ecosystem: [
      { emoji: "🗺️", title: "Maps",       body: "Territórios e denominações" },
      { emoji: "📚", title: "Content",    body: "Conhecimento do vinho e das bebidas" },
      { emoji: "🎓", title: "Learning",   body: "Pedagogia e formação" },
      { emoji: "🎮", title: "Play",       body: "Quizzes e gamificação" },
      { emoji: "🍷", title: "Experience", body: "Eventos e percursos interativos" },
    ],
    s12_signature: "Um ecossistema. Uma camada de experiência.",
    s13_eyebrow: "13 · Porquê a geoVINUM",
    s13_title_a: "A experiência é digital.",
    s13_title_b: "A memória é real.",
    s13_intro: "geoVINUM combina:",
    why_stack: ["Cultura do vinho", "Tecnologia", "Conteúdos", "Gamificação", "Design de experiência"],
    s13_bullets: ["Não é apenas uma ferramenta de bilheteira.", "Não é apenas um quiz.", "Não é apenas uma app."],
    s13_signature: "É um motor de experiências.",
    s14_eyebrow: "14 · Distribuição",
    s14_title_a: "A sua experiência. A sua audiência.",
    s14_title_b: "O seu link.",
    s14_sub: "Uma vez criada, a sua experiência pode ser difundida em qualquer sítio.",
    distribution: ["O seu site", "As suas redes sociais", "Os seus emails", "As suas newsletters", "Os seus QR", "As suas campanhas", "Os seus eventos físicos"],
    s14_pro: "O profissional partilha o seu link geoVINUM Experience.",
    s14_participant: "O participante entra diretamente na experiência.",
    s15_kicker: "15 · A verdadeira mudança",
    s15_title_a: "Pare de vender a garrafa.",
    s15_title_b: "Comece a vender a experiência.",
    pivot_pairs: [
      { bottle: "vende-se.",            exp: "vive-se." },
      { bottle: "prova-se.",            exp: "descobre-se." },
      { bottle: "pode ser esquecida.",  exp: "pode ser contada." },
    ],
    s15_bottle: "Uma garrafa",
    s15_exp: "Uma experiência",
    s15_signature_a: "O produto já não é o destino.",
    s15_signature_b: "Faz parte da viagem.",
    s16_eyebrow: "16 · Manifesto",
    s16_title_a: "Não sirva apenas uma bebida.",
    s16_title_b: "Crie uma aventura.",
    manifesto_pairs: [
      ["Não organize apenas um evento.",  "Crie uma razão para participar."],
      ["Não apresente apenas um produto.","Crie uma razão para descobri-lo."],
      ["Não venda apenas uma garrafa.",   "Venda a experiência que a envolve."],
    ],
    s17_eyebrow: "17 · Passe à ação",
    s17_title_a: "Que experiência",
    s17_title_b: "vai criar?",
    s17_diy_title: "Faça o próprio",
    s17_diy_body: "Construa a sua própria experiência com a geoVINUM.",
    s17_diy_cta: "Começar a criar",
    s17_custom_title: "Construímo-la por si",
    s17_custom_body: "Conte-nos a sua ideia. Transformamo-la em experiência.",
    s17_custom_cta: "Construir a minha experiência",
    faq: [
      { q: "O que é a geoVINUM Experience?",             a: "geoVINUM Experience é ao mesmo tempo uma plataforma e um serviço de design que permite criar experiências interativas em torno do vinho, das bebidas, das marcas, dos territórios e dos eventos: caças ao tesouro, escape games, provas, feiras, masterclasses, ativações de marca, percursos territoriais, desafios gamificados." },
      { q: "Posso criar uma experiência sozinho?",        a: "Sim. O modo self-service permite construir a sua experiência sozinho com as ferramentas geoVINUM: percursos, conteúdos, quizzes, jogos, equipas, desafios, gamificação, experiências de prova." },
      { q: "Podem desenhar a experiência por mim?",       a: "Sim. No modo à medida, a nossa equipa trata do conceito, storytelling, UX/percurso, conteúdos, gamificação, experiência digital, implementação e animação. Ideal para eventos exigentes ou operações de marca." },
      { q: "Uma experiência pode ser reutilizada?",       a: "Sim. Cada experiência pode ser pontual (noite, prova, feira, masterclass) ou recorrente (percurso turístico, animação mensal, experiência sazonal, programa pedagógico, animação em quinta, experiência de marca). Construa uma vez. Viva-a de novo e de novo." },
      { q: "Como acede o participante?",                  a: "Cada experiência tem o seu próprio link geoVINUM Experience. O profissional partilha-o pelo site, redes sociais, emails, newsletter, um QR, uma campanha ou um evento físico. O participante abre-o no smartphone e entra directamente no percurso." },
      { q: "Como se mede o engagement?",                  a: "geoVINUM Experience acompanha em tempo real participantes, horários, vinhos, stocks, pontos, equipas, quizzes, leituras QR e estatísticas. Você pilota o evento e mede o seu impacto." },
      { q: "Podemos criar experiências sem álcool?",      a: "Sim. O formato «sem ou pouco álcool» permite criar experiências de descoberta com ou sem álcool — útil para um público jovem, uma clientela familiar ou uma programação dry." },
    ],
  },
};

// Icônes réutilisables (indépendantes de la langue).
const USECASE_ICONS   = [KeyRound, Puzzle, Wine, Building2, GraduationCap, Tag, MapIcon, Trophy, Leaf];
const AUDIENCE_ICONS  = [Wine, Users, Tag, Building2, Landmark, School];
const STEP_ICONS      = [Lightbulb, Wrench, Share2, Smartphone, Sparkles, BarChart3];
const GAIN_ICONS      = [Users, Sparkles, GraduationCap, Trophy, Repeat, BarChart3, Wine];
const ECO_ICONS       = [MapIcon, ClipboardList, GraduationCap, Play, Wand2];
const ECO_LINKS       = ["/shop", "/blog", "/app#academy", "/app#quiz", "/experience"];

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
const Eyebrow = ({ children, tone = "pink" }) => (
  <div
    className={`text-[11px] uppercase tracking-[0.22em] font-medium mb-4 ${
      tone === "white" ? "text-white/70" : "text-[color:var(--gv-pink)]"
    }`}
  >
    {children}
  </div>
);

const HugeHeadline = ({ children, tone = "ink", className = "" }) => (
  <h2
    className={`font-display leading-[0.98] tracking-tight text-4xl sm:text-5xl lg:text-6xl ${
      tone === "white" ? "text-white" : "text-[color:var(--gv-ink)]"
    } ${className}`}
  >
    {children}
  </h2>
);

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
const ExperiencePage = () => {
  const lang = useLang() || "fr";
  const c = COPY[lang] || COPY.fr;

  return (
    <Layout testId="page-experience">
      <SEO
        title={c.meta_title}
        description={c.meta_desc}
        breadcrumbs={[
          { name: c.breadcrumb_home, url: "/" },
          { name: "geoVINUM Experience", url: "/experience" },
        ]}
        jsonLdList={[
          geovinumOrganizationJsonLd(),
          faqPageJsonLd(c.faq),
        ]}
      />

      {/* 01 — HERO */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 bg-[color:var(--gv-ink)] text-white overflow-hidden" data-testid="exp-hero">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 20% 10%, #D72E5C 0%, transparent 40%), radial-gradient(circle at 80% 70%, #2E5067 0%, transparent 50%)",
        }} />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-8" data-testid="exp-logo">
            <span className="font-display text-2xl md:text-3xl tracking-tight">
              <span className="text-[color:var(--gv-pink)]">geo</span>
              <span className="text-white">VINUM</span>
            </span>
            <span className="text-[11px] uppercase tracking-[0.28em] text-[color:var(--gv-pink)] border-l border-white/20 pl-3 font-medium">
              Experience
            </span>
          </div>
          <h1 className="font-display leading-[0.92] tracking-tight text-5xl sm:text-6xl lg:text-7xl mb-6" data-testid="exp-title">
            {c.hero_title_a}
            <br />
            <span className="text-[color:var(--gv-pink)]">{c.hero_title_b}</span>
          </h1>
          <p className="text-[16.5px] md:text-[18px] text-white/80 max-w-2xl leading-relaxed mb-10">
            {c.hero_desc_pre}<strong className="text-white">{c.hero_desc_strong}</strong>{c.hero_desc_post}
          </p>
          <div className="grid md:grid-cols-2 gap-4 mb-10">
            <div className="bg-white/5 border border-white/10 rounded-[5px] p-5">
              <div className="text-2xl mb-1.5">🧩</div>
              <div className="font-display text-lg text-white mb-1">{c.hero_diy_title}</div>
              <p className="text-[13.5px] text-white/70 leading-relaxed">{c.hero_diy_desc}</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-[5px] p-5">
              <div className="text-2xl mb-1.5">✨</div>
              <div className="font-display text-lg text-white mb-1">{c.hero_custom_title}</div>
              <p className="text-[13.5px] text-white/70 leading-relaxed">{c.hero_custom_desc}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href={CREATE_URL} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[14px] font-medium hover:bg-[color:var(--gv-pink-dark)] transition-colors" data-testid="exp-hero-cta-diy">
              {c.hero_cta_diy} <ArrowRight size={14} />
            </a>
            <Link to={CONTACT_URL} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-[5px] bg-white text-[color:var(--gv-ink)] text-[14px] font-medium hover:bg-white/90 transition-colors" data-testid="exp-hero-cta-custom">
              {c.hero_cta_custom} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="mt-10 text-[12.5px] text-white/50 tracking-wide">{c.hero_ribbon}</div>
        </div>
      </section>

      {/* 02 — LE CHANGEMENT */}
      <section className="py-20 md:py-28 bg-white" data-testid="exp-shift">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Eyebrow>{c.s2_eyebrow}</Eyebrow>
          <HugeHeadline>
            {c.s2_title_a}<br />
            <em className="text-[color:var(--gv-pink)] not-italic">{c.s2_title_b}</em>
          </HugeHeadline>
          <p className="text-[15px] md:text-[16px] text-[color:var(--gv-ink)]/75 leading-relaxed mt-8 max-w-2xl mx-auto">{c.s2_intro}</p>
          <div className="mt-4 mb-8 inline-flex flex-wrap items-center justify-center gap-3 font-mono text-[13px] text-[color:var(--gv-ink)]/55">
            {c.s2_flow.map((v, i) => (
              <span key={v} className="inline-flex items-center gap-3">
                <span>{v}</span>
                {i < c.s2_flow.length - 1 && <ArrowRight size={13} />}
              </span>
            ))}
          </div>
          <p className="text-[16px] text-[color:var(--gv-ink)]/85 leading-relaxed mt-4 max-w-2xl mx-auto whitespace-pre-line">{c.s2_pivot}</p>
          <div className="mt-10 text-[11px] uppercase tracking-[0.22em] text-[color:var(--gv-ink)]/50 mb-3">{c.s2_today}</div>
          <div className="flex flex-wrap items-center justify-center gap-2 max-w-2xl mx-auto">
            {c.s2_chips.map((k) => (
              <span key={k} className="px-3 py-1.5 rounded-[5px] bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] text-[13px] text-[color:var(--gv-ink)]/85">{k}</span>
            ))}
          </div>
          <div className="mt-10 font-display text-2xl md:text-3xl text-[color:var(--gv-pink)]">{c.s2_signature}</div>
        </div>
      </section>

      {/* 03 — LA SOLUTION */}
      <section className="py-20 md:py-28 bg-[color:var(--gv-mist)]/40 border-y border-[color:var(--gv-line)]" data-testid="exp-solution">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <Eyebrow>{c.s3_eyebrow}</Eyebrow>
            <HugeHeadline>{c.s3_title_pre}<em className="text-[color:var(--gv-pink)] not-italic">{c.s3_title_em}</em></HugeHeadline>
            <p className="text-[15.5px] text-[color:var(--gv-ink)]/75 leading-relaxed mt-8 max-w-3xl mx-auto">{c.s3_lead}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-8 flex flex-col" data-testid="exp-solution-diy">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--gv-blue)] mb-3">{c.s3_diy_kicker}</div>
              <div className="font-display text-2xl text-[color:var(--gv-ink)] mb-3">{c.s3_diy_title}</div>
              <p className="text-[14.5px] text-[color:var(--gv-ink)]/75 leading-relaxed flex-1">{c.s3_diy_body}</p>
              <a href={CREATE_URL} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 text-[color:var(--gv-pink)] font-medium text-[14px] hover:gap-3 transition-all">{c.s3_diy_cta} <ArrowRight size={14} /></a>
            </div>
            <div className="bg-[color:var(--gv-ink)] text-white rounded-[6px] p-8 flex flex-col" data-testid="exp-solution-custom">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--gv-pink)] mb-3">{c.s3_custom_kicker}</div>
              <div className="font-display text-2xl mb-3">{c.s3_custom_title}</div>
              <p className="text-[14.5px] text-white/80 leading-relaxed flex-1">{c.s3_custom_body}</p>
              <Link to={CONTACT_URL} className="mt-6 inline-flex items-center gap-2 text-[color:var(--gv-pink)] font-medium text-[14px] hover:gap-3 transition-all">{c.s3_custom_cta} <ArrowRight size={14} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* 04 — USE CASES */}
      <section className="py-20 md:py-28 bg-white" data-testid="exp-usecases">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <Eyebrow>{c.s4_eyebrow}</Eyebrow>
            <HugeHeadline>{c.s4_title_a} <em className="text-[color:var(--gv-pink)] not-italic">{c.s4_title_b}</em></HugeHeadline>
            <p className="text-[15px] text-[color:var(--gv-ink)]/70 mt-6 max-w-2xl mx-auto">{c.s4_sub}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.usecases.map((u, i) => {
              const Icon = USECASE_ICONS[i];
              return (
                <div key={u.title} className="group bg-white border border-[color:var(--gv-line)] rounded-[6px] p-6 hover:border-[color:var(--gv-pink)] hover:shadow-[0_18px_40px_-24px_rgba(215,46,92,0.35)] transition-all" data-testid={`exp-usecase-${i}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <Icon size={20} className="text-[color:var(--gv-pink)]" />
                  </div>
                  <div className="font-display text-xl text-[color:var(--gv-ink)] mb-2 uppercase tracking-wide">{u.title}</div>
                  <p className="text-[13.5px] text-[color:var(--gv-ink)]/75 leading-relaxed">{u.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 05 — DEUX MODES */}
      <section className="py-20 md:py-28 bg-[color:var(--gv-mist)]/40 border-y border-[color:var(--gv-line)]" data-testid="exp-modes">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <Eyebrow>{c.s5_eyebrow}</Eyebrow>
            <HugeHeadline>{c.s5_title_a}<br /><em className="text-[color:var(--gv-pink)] not-italic">{c.s5_title_b}</em></HugeHeadline>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-7 md:p-8">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--gv-blue)] mb-3">{c.s5_diy_kicker}</div>
              <div className="font-display text-2xl text-[color:var(--gv-ink)] mb-3">{c.s5_diy_title}</div>
              <p className="text-[14px] text-[color:var(--gv-ink)]/70 leading-relaxed mb-5">{c.s5_diy_body}</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {c.s5_diy_items.map((it) => (
                  <span key={it} className="px-3 py-1 rounded-[4px] bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] text-[12.5px] text-[color:var(--gv-ink)]/85">{it}</span>
                ))}
              </div>
              <p className="text-[14px] text-[color:var(--gv-ink)]/85 mb-6"><strong>{c.s5_diy_you}</strong></p>
              <a href={CREATE_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[5px] bg-[color:var(--gv-ink)] text-white text-[13.5px] font-medium hover:bg-[color:var(--gv-blue)] transition-colors">{c.s5_diy_cta} <ArrowRight size={14} /></a>
            </div>
            <div className="bg-[color:var(--gv-ink)] text-white rounded-[6px] p-7 md:p-8">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--gv-pink)] mb-3">{c.s5_custom_kicker}</div>
              <div className="font-display text-2xl mb-3">{c.s5_custom_title}</div>
              <p className="text-[14px] text-white/70 leading-relaxed mb-5">{c.s5_custom_body}</p>
              <ol className="space-y-1.5 mb-6 text-[13.5px] text-white/90">
                {c.s5_custom_pipeline.map((step, i) => (
                  <li key={step} className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-white/10 border border-white/20 text-[11px] flex items-center justify-center shrink-0">{i + 1}</span>
                    {step}
                  </li>
                ))}
              </ol>
              <Link to={CONTACT_URL} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[13.5px] font-medium hover:bg-[color:var(--gv-pink-dark)] transition-colors">{c.s5_custom_cta} <ArrowRight size={14} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* 06 — CADENCE */}
      <section className="py-20 md:py-28 bg-white" data-testid="exp-cadence">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <Eyebrow>{c.s6_eyebrow}</Eyebrow>
            <HugeHeadline>{c.s6_title_a}<br /><em className="text-[color:var(--gv-pink)] not-italic">{c.s6_title_b}</em></HugeHeadline>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-7">
              <div className="flex items-center gap-2 mb-4">
                <Zap size={18} className="text-[color:var(--gv-pink)]" />
                <div className="font-display text-xl text-[color:var(--gv-ink)]">{c.s6_oneshot_title}</div>
              </div>
              <ul className="space-y-1.5 text-[14px] text-[color:var(--gv-ink)]/80">
                {c.s6_oneshot.map((it) => (
                  <li key={it} className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 text-[color:var(--gv-pink)] shrink-0" />{it}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-7">
              <div className="flex items-center gap-2 mb-4">
                <Repeat size={18} className="text-[color:var(--gv-pink)]" />
                <div className="font-display text-xl text-[color:var(--gv-ink)]">{c.s6_recurring_title}</div>
              </div>
              <ul className="space-y-1.5 text-[14px] text-[color:var(--gv-ink)]/80">
                {c.s6_recurring.map((it) => (
                  <li key={it} className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 text-[color:var(--gv-pink)] shrink-0" />{it}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="text-center mt-12 font-display text-2xl md:text-3xl text-[color:var(--gv-pink)]">{c.s6_signature}</div>
        </div>
      </section>

      {/* 07 — HOW */}
      <section className="py-20 md:py-28 bg-[color:var(--gv-mist)]/40 border-y border-[color:var(--gv-line)]" data-testid="exp-how">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <Eyebrow>{c.s7_eyebrow}</Eyebrow>
            <HugeHeadline>{c.s7_title_a}<em className="text-[color:var(--gv-pink)] not-italic">{c.s7_title_b}</em></HugeHeadline>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.steps.map((s, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <div key={s.num} className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-display text-3xl text-[color:var(--gv-pink)]">{s.num}</span>
                    <Icon size={17} className="text-[color:var(--gv-ink)]/40" />
                  </div>
                  <div className="font-display text-lg text-[color:var(--gv-ink)] uppercase tracking-wide mb-2">{s.title}</div>
                  <p className="text-[13.5px] text-[color:var(--gv-ink)]/75 leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 08 — ADVENTURE */}
      <section className="py-20 md:py-28 bg-white" data-testid="exp-adventure">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <Eyebrow>{c.s8_eyebrow}</Eyebrow>
            <HugeHeadline>{c.s8_title_a}<br /><em className="text-[color:var(--gv-pink)] not-italic">{c.s8_title_b}</em></HugeHeadline>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-[color:var(--gv-mist)]/50 border border-[color:var(--gv-line)] rounded-[6px] p-7">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--gv-ink)]/45 mb-5">{c.s8_before}</div>
              <ul className="space-y-3">
                {c.before_flow.map((step, i) => (
                  <li key={step} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-white border border-[color:var(--gv-line)] text-[11px] font-mono text-[color:var(--gv-ink)]/50 flex items-center justify-center shrink-0">{i + 1}</span>
                    <span className="font-mono text-[13.5px] text-[color:var(--gv-ink)]/60 uppercase tracking-wide">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[color:var(--gv-ink)] text-white rounded-[6px] p-7">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--gv-pink)] mb-5">{c.s8_after}</div>
              <ul className="space-y-2.5">
                {c.after_flow.map((step, i) => (
                  <li key={step} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[color:var(--gv-pink)] text-[11px] font-mono flex items-center justify-center shrink-0">{i + 1}</span>
                    <span className="font-mono text-[13.5px] uppercase tracking-wide">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="text-center mt-12 text-[16px] text-[color:var(--gv-ink)]/80">
            {c.s8_signature_a} <em className="text-[color:var(--gv-pink)] not-italic font-semibold">{c.s8_signature_b}</em>
          </div>
        </div>
      </section>

      {/* 09 — CASE */}
      <section className="py-20 md:py-28 bg-[color:var(--gv-mist)]/40 border-y border-[color:var(--gv-line)]" data-testid="exp-case">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <Eyebrow>{c.s9_eyebrow}</Eyebrow>
            <HugeHeadline>{c.s9_title_a}<em className="text-[color:var(--gv-pink)] not-italic">{c.s9_title_b}</em></HugeHeadline>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
              <span className="px-3 py-1.5 rounded-[5px] bg-white border border-[color:var(--gv-line)] text-[12.5px] text-[color:var(--gv-ink)]/85 font-medium">{c.s9_chip_a}</span>
              <span className="px-3 py-1.5 rounded-[5px] bg-white border border-[color:var(--gv-line)] text-[12.5px] text-[color:var(--gv-ink)]/85 font-medium">{c.s9_chip_b}</span>
            </div>
          </div>
          {/* Real screenshots from the operator's back-office — shows the
              actual product running the event (ticket levels + QR share
              and wine management). Adds huge credibility to the case
              study. */}
          <div className="grid md:grid-cols-2 gap-4 mb-10">
            <figure className="rounded-[8px] overflow-hidden border border-[color:var(--gv-line)] bg-white shadow-[0_20px_50px_-25px_rgba(46,80,103,0.35)]" data-testid="exp-case-screen-billets">
              <img
                src={CASE_SCREENS.billets}
                alt="Back-office geoVINUM Experience — gestion des billets et lien de partage"
                loading="lazy"
                className="w-full h-auto block"
              />
            </figure>
            <figure className="rounded-[8px] overflow-hidden border border-[color:var(--gv-line)] bg-white shadow-[0_20px_50px_-25px_rgba(46,80,103,0.35)]" data-testid="exp-case-screen-vins">
              <img
                src={CASE_SCREENS.vins}
                alt="Back-office geoVINUM Experience — gestion des vins, doses et quiz pédagogique"
                loading="lazy"
                className="w-full h-auto block"
              />
            </figure>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-7">
              <div className="flex items-center gap-2 mb-4"><Users size={17} className="text-[color:var(--gv-pink)]" /><div className="font-display text-xl text-[color:var(--gv-ink)]">{c.s9_part_title}</div></div>
              <ul className="space-y-1.5 text-[13.5px] text-[color:var(--gv-ink)]/80">
                {c.s9_part_items.map((it) => (
                  <li key={it} className="flex items-start gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-[color:var(--gv-pink)] shrink-0" />{it}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-7">
              <div className="flex items-center gap-2 mb-4"><ClipboardList size={17} className="text-[color:var(--gv-pink)]" /><div className="font-display text-xl text-[color:var(--gv-ink)]">{c.s9_org_title}</div></div>
              <div className="flex flex-wrap gap-2">
                {c.s9_org_items.map((it) => (
                  <span key={it} className="px-2.5 py-1 rounded-[4px] bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] text-[12.5px] text-[color:var(--gv-ink)]/85">{it}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="text-center mt-10 font-display text-2xl md:text-3xl text-[color:var(--gv-pink)]">{c.s9_signature}</div>
        </div>
      </section>

      {/* 10 — AUDIENCES */}
      <section className="py-20 md:py-28 bg-white" data-testid="exp-audiences">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <Eyebrow>{c.s10_eyebrow}</Eyebrow>
            <HugeHeadline>{c.s10_title_a} <em className="text-[color:var(--gv-pink)] not-italic">{c.s10_title_b}</em></HugeHeadline>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.audiences.map((a, i) => (
              <div key={a.title} className="bg-[color:var(--gv-mist)]/40 border border-[color:var(--gv-line)] rounded-[6px] p-6 hover:border-[color:var(--gv-pink)] transition-colors" data-testid={`exp-audience-${i}`}>
                <div className="text-2xl mb-3" aria-hidden>{a.emoji}</div>
                <div className="font-display text-lg text-[color:var(--gv-ink)] uppercase tracking-wide mb-2">{a.title}</div>
                <p className="text-[13.5px] text-[color:var(--gv-ink)]/75 leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11 — GAINS */}
      <section className="py-20 md:py-28 bg-[color:var(--gv-mist)]/40 border-y border-[color:var(--gv-line)]" data-testid="exp-gains">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <Eyebrow>{c.s11_eyebrow}</Eyebrow>
            <HugeHeadline>{c.s11_title_a}<br /><em className="text-[color:var(--gv-pink)] not-italic">{c.s11_title_b}</em></HugeHeadline>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {c.gains.map((g, i) => {
              const Icon = GAIN_ICONS[i];
              return (
                <div key={g.title} className={`bg-white border border-[color:var(--gv-line)] rounded-[6px] p-6 ${i === 6 ? "md:col-span-2 lg:col-span-3" : ""}`} data-testid={`exp-gain-${i}`}>
                  <div className="w-10 h-10 rounded-[6px] bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)] flex items-center justify-center mb-4"><Icon size={17} /></div>
                  <div className="font-display text-lg text-[color:var(--gv-ink)] uppercase tracking-wide mb-2">{g.title}</div>
                  <p className="text-[13.5px] text-[color:var(--gv-ink)]/75 leading-relaxed">{g.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 12 — ECOSYSTEM */}
      <section className="py-20 md:py-28 bg-white" data-testid="exp-ecosystem">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <Eyebrow>{c.s12_eyebrow}</Eyebrow>
            <HugeHeadline>{c.s12_title_a} <em className="text-[color:var(--gv-pink)] not-italic">{c.s12_title_b}</em></HugeHeadline>
            <p className="text-[15px] text-[color:var(--gv-ink)]/70 mt-6 max-w-2xl mx-auto">{c.s12_sub}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {c.ecosystem.map((e, i) => {
              const Icon = ECO_ICONS[i];
              const isExp = e.title === "Experience";
              return (
                <Link key={e.title} to={ECO_LINKS[i]} className={`block rounded-[6px] p-5 border transition-all ${isExp ? "bg-[color:var(--gv-pink)]/5 border-[color:var(--gv-pink)]/40 hover:border-[color:var(--gv-pink)]" : "bg-[color:var(--gv-mist)]/40 border-[color:var(--gv-line)] hover:border-[color:var(--gv-pink)]"}`}>
                  <div className="text-2xl mb-3" aria-hidden>{e.emoji}</div>
                  <div className={`font-display text-lg uppercase tracking-wide mb-1.5 ${isExp ? "text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]"}`}>{e.title}</div>
                  <p className="text-[12.5px] text-[color:var(--gv-ink)]/70 leading-relaxed">{e.body}</p>
                  <Icon size={13} className={`mt-4 ${isExp ? "text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]/40"}`} />
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-12 font-display text-2xl md:text-3xl text-[color:var(--gv-pink)]">{c.s12_signature}</div>
        </div>
      </section>

      {/* 13 — WHY */}
      <section className="py-20 md:py-28 bg-[color:var(--gv-ink)] text-white" data-testid="exp-why">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Eyebrow tone="white">{c.s13_eyebrow}</Eyebrow>
          <HugeHeadline tone="white">{c.s13_title_a}<br /><em className="text-[color:var(--gv-pink)] not-italic">{c.s13_title_b}</em></HugeHeadline>
          <p className="text-[15px] text-white/70 mt-8 mb-10">{c.s13_intro}</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
            {c.why_stack.map((k, i) => (
              <span key={k} className="inline-flex items-center gap-3">
                <span className="px-3 py-1.5 rounded-[5px] bg-white/5 border border-white/15 text-[13px] text-white/90">{k}</span>
                {i < c.why_stack.length - 1 && <span className="text-[color:var(--gv-pink)] font-mono">×</span>}
              </span>
            ))}
          </div>
          <div className="space-y-1.5 text-[14.5px] text-white/70 mb-8">
            {c.s13_bullets.map((b) => <p key={b}>{b}</p>)}
          </div>
          <div className="font-display text-2xl md:text-3xl text-[color:var(--gv-pink)]">{c.s13_signature}</div>
        </div>
      </section>

      {/* 14 — DISTRIBUTE */}
      <section className="py-20 md:py-28 bg-white" data-testid="exp-distribute">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <Eyebrow>{c.s14_eyebrow}</Eyebrow>
            <HugeHeadline>{c.s14_title_a}<br /><em className="text-[color:var(--gv-pink)] not-italic">{c.s14_title_b}</em></HugeHeadline>
            <p className="text-[15px] text-[color:var(--gv-ink)]/75 mt-6 max-w-2xl mx-auto">{c.s14_sub}</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2.5 mb-10">
            {c.distribution.map((d) => (
              <span key={d} className="px-3 py-1.5 rounded-[5px] bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] text-[13px] text-[color:var(--gv-ink)]/85">{d}</span>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            <div className="bg-[color:var(--gv-mist)]/40 border border-[color:var(--gv-line)] rounded-[6px] p-5 flex items-start gap-3">
              <Share2 size={18} className="text-[color:var(--gv-pink)] shrink-0 mt-0.5" />
              <p className="text-[13.5px] text-[color:var(--gv-ink)]/85 leading-relaxed">{c.s14_pro}</p>
            </div>
            <div className="bg-[color:var(--gv-mist)]/40 border border-[color:var(--gv-line)] rounded-[6px] p-5 flex items-start gap-3">
              <QrCode size={18} className="text-[color:var(--gv-pink)] shrink-0 mt-0.5" />
              <p className="text-[13.5px] text-[color:var(--gv-ink)]/85 leading-relaxed">{c.s14_participant}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 15 — PIVOT */}
      <section className="py-20 md:py-28 bg-[color:var(--gv-pink)] text-white" data-testid="exp-pivot">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/70 mb-4 font-medium">{c.s15_kicker}</div>
          <HugeHeadline tone="white" className="text-white">{c.s15_title_a}<br /><em className="not-italic">{c.s15_title_b}</em></HugeHeadline>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {c.pivot_pairs.map((p, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-[6px] p-6 border border-white/15">
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/60 mb-2">{c.s15_bottle}</div>
                <div className="font-display text-xl mb-4">{p.bottle}</div>
                <div className="w-8 h-px bg-white/30 mx-auto mb-4" />
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/60 mb-2">{c.s15_exp}</div>
                <div className="font-display text-xl">{p.exp}</div>
              </div>
            ))}
          </div>
          <p className="mt-12 text-[16px] text-white/85 max-w-2xl mx-auto leading-relaxed">
            <strong>{c.s15_signature_a}</strong><br />
            <em>{c.s15_signature_b}</em>
          </p>
        </div>
      </section>

      {/* 16 — MANIFESTO */}
      <section className="py-20 md:py-28 bg-white" data-testid="exp-manifesto">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <Eyebrow>{c.s16_eyebrow}</Eyebrow>
          <HugeHeadline>{c.s16_title_a}<br /><em className="text-[color:var(--gv-pink)] not-italic">{c.s16_title_b}</em></HugeHeadline>
          <div className="mt-12 space-y-6 text-left max-w-xl mx-auto">
            {c.manifesto_pairs.map(([a, b]) => (
              <div key={a} className="border-l-2 border-[color:var(--gv-pink)] pl-5">
                <div className="text-[15px] text-[color:var(--gv-ink)]/55 line-through mb-1">{a}</div>
                <div className="font-display text-xl text-[color:var(--gv-ink)]">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 17 — CTA */}
      <section className="py-20 md:py-28 bg-[color:var(--gv-ink)] text-white" data-testid="exp-final-cta">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <Eyebrow tone="white">{c.s17_eyebrow}</Eyebrow>
            <HugeHeadline tone="white">{c.s17_title_a} <em className="text-[color:var(--gv-pink)] not-italic">{c.s17_title_b}</em></HugeHeadline>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/5 border border-white/10 rounded-[6px] p-8 flex flex-col">
              <div className="text-3xl mb-3">🧩</div>
              <div className="font-display text-2xl mb-2">{c.s17_diy_title}</div>
              <p className="text-[14.5px] text-white/75 leading-relaxed flex-1 mb-6">{c.s17_diy_body}</p>
              <a href={CREATE_URL} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-[5px] bg-white text-[color:var(--gv-ink)] text-[14px] font-medium hover:bg-white/90 transition-colors" data-testid="exp-final-diy">{c.s17_diy_cta} <ArrowRight size={14} /></a>
            </div>
            <div className="bg-[color:var(--gv-pink)]/10 border border-[color:var(--gv-pink)]/30 rounded-[6px] p-8 flex flex-col">
              <div className="text-3xl mb-3">✨</div>
              <div className="font-display text-2xl mb-2">{c.s17_custom_title}</div>
              <p className="text-[14.5px] text-white/85 leading-relaxed flex-1 mb-6">{c.s17_custom_body}</p>
              <Link to={CONTACT_URL} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[14px] font-medium hover:bg-[color:var(--gv-pink-dark)] transition-colors" data-testid="exp-final-custom">{c.s17_custom_cta} <ArrowRight size={14} /></Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ExperiencePage;
